import React, { useState } from "react"
import AuthService from "../../services/auth.service"

/*
	Entrar por primera vez u "Olvidé mi contraseña" con un código de 6 dígitos al WhatsApp del teléfono de la ficha
	(Esteban, 2026-10-13; antes solo SMS, 2026-10-12). El correo @washingtonacademy.com solo se lee dentro de WAPP, así que
	el acceso no puede depender de él. Si el servidor ya mandó el código al intentar entrar, se abre directo en el paso 2.
	Desde 2026-10-21 el mismo código también puede llegar por correo (al de WAPP y al personal de la ficha): Esteban quiere
	las dos opciones a la vista, y es la única para los Super Admin, que no reciben códigos por WhatsApp.
*/
type Props = {
	alTerminar: (mensaje: string) => void
	alVolver: () => void
	/** Alumnos que no saben su correo: código al WhatsApp por número (2026-10-21). */
	alPorTelefono?: () => void
	emailInicial?: string
	avisoInicial?: string
}

type Canal = "whatsapp" | "correo"

const RecuperarPorCodigo: React.FC<Props> = ({ alTerminar, alVolver, alPorTelefono, emailInicial, avisoInicial }) => {
	const [paso, setPaso] = useState<"pedir" | "confirmar">(avisoInicial ? "confirmar" : "pedir")
	const [canal, setCanal] = useState<Canal>("whatsapp")
	const [email, setEmail] = useState(emailInicial || "")
	const [codigo, setCodigo] = useState("")
	const [clave, setClave] = useState("")
	const [otra, setOtra] = useState("")
	const [aviso, setAviso] = useState<string | null>(avisoInicial || null)
	const [error, setError] = useState<string | null>(null)
	const [ocupado, setOcupado] = useState(false)

	const textoDe = (x: any, porDefecto: string) => x?.response?.data?.message || porDefecto

	const pedir = async (por: Canal, e?: React.FormEvent) => {
		e && e.preventDefault()
		if (!email.trim()) { setPaso("pedir"); setError("Escribe tu correo de WAPP."); return }
		setOcupado(true); setError(null)
		try {
			const r = await AuthService.pedirCodigo(email.trim(), por)
			setCanal(por)
			setCodigo("")
			setAviso(r.data.message)
			setPaso("confirmar")
		} catch (x: any) {
			setError(textoDe(x, "No se pudo pedir el código. Revisa tu conexión e intenta otra vez."))
		} finally {
			setOcupado(false)
		}
	}

	const confirmar = async (e: React.FormEvent) => {
		e.preventDefault()
		if (clave.length < 8) { setError("La contraseña nueva necesita al menos 8 caracteres."); return }
		if (clave !== otra) { setError("Las contraseñas no coinciden."); return }
		setOcupado(true); setError(null)
		try {
			const r = await AuthService.confirmarCodigo(email.trim(), codigo.trim(), clave, otra)
			alTerminar(r.data.message)
		} catch (x: any) {
			setError(textoDe(x, "No se pudo cambiar la contraseña. Intenta otra vez."))
		} finally {
			setOcupado(false)
		}
	}

	return paso === "pedir" ? (
		<form className="form" onSubmit={(e) => pedir("whatsapp", e)}>
			<h2 className="h1">Olvidé Mi Contraseña</h2>
			{/* Alumnos (la mayoría): por su número, sin saber el correo. Primero y grande (QA 2026-10-21). */}
			{alPorTelefono ? <div className="accOtraForma accPrimero">
				<button type="button" className="accBotonWa" disabled={ocupado} onClick={() => alPorTelefono()}>Usar Mi Número De WhatsApp</button>
			</div> : null}
			<p>{alPorTelefono ? "O escribe tu correo de WAPP y elige por dónde te mandamos el código." : "Escribe tu correo de WAPP y elige por dónde te mandamos el código."}</p>
			<div className="divInput">
				<input placeholder="Correo" type="email" required value={email} disabled={ocupado} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
			</div>
			{error ? <p className="accError">{error}</p> : null}
			<div className="forgotButton">
				<button className="btn btnPurple rounded-pill" style={{ width: "auto" }} type="submit" disabled={ocupado}>Por WhatsApp</button>
				<button className="accBotonWa accBotonPar" type="button" disabled={ocupado} onClick={() => pedir("correo")}>Por Correo</button>
			</div>
			{ocupado ? <p className="accEnviando">Enviando…</p> : null}
			<div className="logInLink">
				<a onClick={() => !ocupado && alVolver()}>Iniciar Sesión</a>
			</div>
		</form>
	) : (
		<form className="form" onSubmit={confirmar}>
			<h2 className="h1">Crea Tu Contraseña</h2>
			{aviso ? <p>{aviso}</p> : null}
			{email ? <p style={{ fontSize: "14px" }}>Cuenta: <strong>{email}</strong></p> : null}
			<div className="divInput">
				<input placeholder="Código de 6 dígitos" inputMode="numeric" autoComplete="one-time-code" maxLength={8} required value={codigo} disabled={ocupado} onChange={(e) => setCodigo(e.target.value)} />
			</div>
			<div className="divInput">
				<input placeholder="Nueva contraseña (mínimo 8)" type="password" autoComplete="new-password" minLength={8} maxLength={50} required value={clave} disabled={ocupado} onChange={(e) => setClave(e.target.value)} />
			</div>
			<div className="divInput">
				<input placeholder="Confirmar contraseña" type="password" autoComplete="new-password" minLength={8} maxLength={50} required value={otra} disabled={ocupado} onChange={(e) => setOtra(e.target.value)} />
			</div>
			{error ? <p className="accError">{error}</p> : null}
			<div className="forgotButton">
				<button className="btn btnPurple rounded-pill" style={{ width: "auto" }} type="submit" disabled={ocupado}>{ocupado ? "Guardando…" : "Guardar Contraseña"}</button>
			</div>
			<div className="logInLink">
				<a onClick={() => !ocupado && pedir(canal)}>Mandar Otro Código</a>
				&nbsp;·&nbsp;
				<a onClick={() => !ocupado && pedir(canal === "correo" ? "whatsapp" : "correo")}>{canal === "correo" ? "Mejor Por WhatsApp" : "Mejor Por Correo"}</a>
			</div>
			<div className="logInLink accSegundaLinea">
				<a onClick={() => !ocupado && alVolver()}>Iniciar Sesión</a>
			</div>
		</form>
	)
}

export default RecuperarPorCodigo
